import {
  Checkbox,
  Input,
  RadioButton,
  Select,
  Typography,
  PrimaryButton,
  SecondaryButton,
} from '@weareopenr/component-library';
import styles from './PublishDetailsView.module.scss';
import { publishTimes } from './shared';
import { IPublishFormValues, PublishType, Steps } from '../ProductPublishModal/types';

import { useEffect } from 'react';
import { Controller, Control, useForm, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import dayjs from 'dayjs';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { getPublishTypeLabel } from 'src/helpers/getPublishTypeLabel/getPublishTypeLabel';
import { formatDateTimeDayJs } from 'src/helpers/formatDateTime/formatDateTimeDayJs';

export interface PublishScheduleFormValues {
  publicationType: publishTimes;
  publicationDate: string; // string is returned from HTML input
  scheduleName: string;
  priceSchemeId: string | null;
}

export const PublishDetailsView = ({
  submitDataToParent,
  defaultPublicationDate,
  publishType,
  defaultPriceSchemeId,
  defaultScheduleName,
}: {
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: number) => void;
  defaultPublicationDate: Date | null;
  publishType: PublishType;
  defaultPriceSchemeId: string | null;
  defaultScheduleName?: string;
}) => {
  const showPriceScheme = publishType === PublishType.PRICING;
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes({ enabled: showPriceScheme });

  const priceSchemeOptions = priceSchemes.map((scheme) => ({
    value: scheme.id,
    name: scheme.name,
  }));

  const {
    watch,
    register,
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<PublishScheduleFormValues>({
    defaultValues: {
      publicationType: defaultPublicationDate ? publishTimes.SCHEDULE : publishTimes.NOW,
      publicationDate: defaultPublicationDate ? dayjs(defaultPublicationDate).format('YYYY-MM-DDTHH:mm') : '',
      scheduleName: defaultScheduleName ?? getPublishTypeLabel(publishType).title,
      priceSchemeId: defaultPriceSchemeId,
    },
  });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name !== 'publicationDate') return;
      const baseTitle = getPublishTypeLabel(publishType).title;
      const dateSuffix = value.publicationDate ? ` - ${dayjs(value.publicationDate).format('DD/MM/YYYY, HH:mm')}` : '';
      setValue('scheduleName', `${baseTitle}${dateSuffix}`);
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, publishType]);

  const onSubmit = (data: PublishScheduleFormValues) => {
    submitDataToParent(
      {
        publicationDate: data.publicationType === publishTimes.SCHEDULE ? new Date(data.publicationDate) : null,
        scheduleName: data.scheduleName,
        ...(showPriceScheme && { priceSchemeId: data.priceSchemeId }),
      },
      Steps.instance,
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ScheduleSelector
        register={register}
        watch={watch}
        control={control}
        showPriceScheme={showPriceScheme}
        isPriceSchemesLoading={isPriceSchemesLoading}
        priceSchemeOptions={priceSchemeOptions}
      />
      <ButtonContainer
        left={
          <SecondaryButton
            onClick={() => {
              submitDataToParent({}, Steps.type);
            }}
            size="full-width"
          >
            Back
          </SecondaryButton>
        }
        right={
          <PrimaryButton type="submit" size="full-width" disabled={!isValid}>
            Continue
          </PrimaryButton>
        }
      />
    </form>
  );
};

interface ScheduleSelectorProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  control?: Control<any>;
  showPriceScheme?: boolean;
  isPriceSchemesLoading?: boolean;
  priceSchemeOptions?: { value: string; name: string }[];
  showConfirmationCheckbox?: boolean;
  title?: string;
  titleSize?: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall';
  titleWeight?: 'regular' | 'semi-bold' | 'bold';
  dateLabel?: string;
  showScheduleName?: boolean;
  nowLabel?: string;
  scheduleLabel?: string;
  radioContainerClassName?: string;
  titleClassName?: string;
}

export const ScheduleSelector = ({
  register,
  watch,
  control,
  showPriceScheme,
  isPriceSchemesLoading,
  priceSchemeOptions,
  showConfirmationCheckbox,
  title = 'Publish Set Up',
  titleSize = 'xxlarge',
  titleWeight = 'bold',
  dateLabel = 'Date time',
  showScheduleName: showScheduleNameProp,
  nowLabel = 'Publish now',
  scheduleLabel = 'Schedule publish for future',
  radioContainerClassName,
  titleClassName,
}: ScheduleSelectorProps) => {
  const showScheduleName = showScheduleNameProp ?? !showConfirmationCheckbox;
  const watchPublishType = watch('publicationType');
  return (
    <>
      <Typography
        weight={titleWeight}
        size={titleSize}
        className={[styles['subheader'], titleClassName].filter(Boolean).join(' ')}
      >
        {title}
      </Typography>
      <div className={[styles['radio-container'], radioContainerClassName].filter(Boolean).join(' ')}>
        <RadioButton name="publicationType" register={register} value={publishTimes.NOW}>
          {nowLabel}
        </RadioButton>
        <RadioButton name="publicationType" register={register} value={publishTimes.SCHEDULE}>
          {scheduleLabel}
        </RadioButton>
        {watchPublishType === publishTimes.SCHEDULE && (
          <div className={styles['schedule-container']}>
            <Input
              type="datetime-local"
              label={dateLabel}
              register={register('publicationDate', {
                required: 'Publication date is required',
                validate: (value: string) => {
                  if (!value) {
                    return 'Publication date is required';
                  }
                  return dayjs(value).isAfter(dayjs()) || 'Publication date must be in the future';
                },
              })}
            />
            {showScheduleName && <Input type="text" label="Schedule name" register={register('scheduleName')} />}
            {showConfirmationCheckbox && (
              <Checkbox
                labelWeight="regular"
                name="confirmationCheckbox"
                register={register}
                required
                label={`I acknowledge that this publish will use a snapshot of the selected data as of ${formatDateTimeDayJs(new Date())}. Changes made after this time will require a new publish.`}
              />
            )}
          </div>
        )}
        {showPriceScheme && control && (
          <>
            <Typography className={styles['price-scheme-title']} weight="semi-bold">
              Price scheme details
            </Typography>
            <Controller
              name="priceSchemeId"
              control={control}
              rules={{ required: 'Please select a price scheme' }}
              render={({ field, fieldState: { error } }) => (
                <Select
                  label="Price Scheme"
                  loading={isPriceSchemesLoading}
                  disabled={isPriceSchemesLoading}
                  options={priceSchemeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          </>
        )}
      </div>
    </>
  );
};
